import { Button, Grid, Stack, Typography } from '@mui/material'
import { omit, path, pick, prop } from 'ramda'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Case, Switch } from 'react-if'
import { useNavigate, useParams } from 'react-router-dom'

import { Option, VailAccordion, VailSelect, VailTextField } from '@/components'
import LanguageSelector from '@/components/LanguageSelector'
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useListHistory, useOrg, useUrlParams } from '@/hooks'
import WorkshopTitlePreview from '@/screens/ComponentLibrary/components/WorkshopTitlePreview'
import {
  useCreateCustomComponent,
  useGetCustomComponent,
  useListCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { safeCypressID } from '@/utils'
import { canUseName } from '@/utils'
import { ComponentType, Visibility } from '@/utils/api'

import { SupportedLocales } from '../../types/customComponents'
import {
  DisplayEGsBy,
  DisplayMenu,
  OrderBy,
  TaskTrackerButtons,
  taskTrackerForm,
  TaskTrackerSettings,
} from '../types'

export const Settings = () => {
  const { orgID } = useParams()
  const { componentID } = useComponentProvider()
  const [{ lang = 'en' }] = useUrlParams()
  const org = useOrg(orgID)

  const navigate = useNavigate()
  const current = useGetCustomComponent<TaskTrackerSettings>(componentID)
  const create = useCreateCustomComponent<TaskTrackerSettings>(orgID)
  const update = useUpdateCustomComponent<TaskTrackerSettings>(componentID)
  const form = useForm<taskTrackerForm>({
    defaultValues: {},
  })
  const { reset } = form
  useEffect(() => {
    if (!current.data) return
    const { name, description, settings } = current.data

    reset({
      description,
      name,
      settings,
    })
  }, [current.data, reset])

  const onSubmit = form.handleSubmit(async (data) => {
    const safeData = pick(['name', 'description', 'settings'], data)

    const conversation = path(['settings', 'conversation'], safeData)

    const shouldAdd = (
      conversation: TaskTrackerSettings['conversation'],
      section: keyof TaskTrackerSettings['conversation']
    ) => {
      const partial = prop(section, conversation) as TaskTrackerButtons
      if (!partial) return {}

      const preMenuText = path(['preMenuText', 'en', 0], partial)
      const postMenuText = path(['postMenuText', 'en', 0], partial)

      return {
        [section]: {
          ...pick(['button'], partial),
          ...(!!preMenuText ? pick(['preMenuText'], partial) : {}),
          ...(!!postMenuText ? pick(['postMenuText'], partial) : {}),
        },
      }
    }

    const dataToSave: taskTrackerForm = {
      ...safeData,
      settings: {
        ...safeData.settings,
        conversation: {
          ...omit(
            ['unstarted', 'noEGs', 'complete', 'inProgress'],
            conversation
          ),
          ...shouldAdd(conversation, 'unstarted'),

          ...shouldAdd(conversation, 'complete'),
          ...shouldAdd(conversation, 'inProgress'),
          noEGs: !!conversation.noEGs?.text?.en?.[0]
            ? pick(['button', 'text'], conversation.noEGs)
            : null,
        },
      },
    }

    if (componentID)
      return await update.mutateAsync({ ...dataToSave, id: componentID })
    const res = await create.mutateAsync({
      ...dataToSave,
      type: ComponentType.taskTracker,
    })

    return navigate(`../${res?.id}`)
  })
  const recipes = useListCustomComponent(
    orgID,
    {
      type: { eq: ComponentType.recipe },
      visibility: { ne: Visibility.archived },
    },
    ['recipes']
  )
  const name = form.watch('settings.customRecipeOffRamp.recipeName')
  const historyItem = useListHistory(['history', name], 'customComponent', {
    id: name,
  })

  const recipesOptions: Option[] =
    recipes.data?.map((component) => ({
      label: component.name,
      value: component.id,
    })) ?? []

  const languagesSupported = org.data?.languagesSupported || ['en']
  return (
    <FormProvider {...form}>
      <Stack
        component="form"
        height="100%"
        justifyContent="start"
        rowGap={2}
        onSubmit={onSubmit}
      >
        <Button
          id={safeCypressID('save task tracker')}
          sx={{ alignSelf: 'flex-end', mb: 2 }}
          type="submit"
        >
          Save
        </Button>
        <VailAccordion
          defaultExpanded
          useDefaultBox
          detailsSx={{
            display: 'flex',
            flexDirection: ['column', null, 'row'],
            gap: 2,
          }}
          title="Description"
        >
          <Stack flex={1} gap={2}>
            <VailTextField
              control={form.control}
              label="Name"
              name="name"
              rules={{
                validate: {
                  canUseName: async (data) =>
                    canUseName(orgID, componentID, data),
                },
              }}
            />
            <WorkshopTitlePreview name={form.watch('name')} />
            <VailSelect
              disabled
              defaultValue={ComponentType.taskTracker}
              label="Type"
              name="type"
              options={[
                {
                  label: 'Task Tracker',
                  value: ComponentType.taskTracker,
                },
              ]}
            />
          </Stack>
          <Stack flex={1}>
            <VailTextField
              multiline
              control={form.control}
              label="Description"
              maxRows={5}
              minRows={5}
              name="description"
              rules={{ required: false }}
            />
          </Stack>
        </VailAccordion>
        <VailAccordion
          defaultExpanded
          useDefaultBox
          detailsSx={{
            display: 'flex',
            flexDirection: ['column', null, 'row'],
            gap: 2,
          }}
          title="Configuration"
        >
          <Stack
            flex={1}
            gap={2}
            justifyContent="start"
            sx={{ alignItems: 'center' }}
          >
            <LanguageSelector />
            <Stack
              flexDirection={{ md: 'row', xs: 'column' }}
              gap={2}
              sx={{
                alignItems: 'center',
                flex: 1,
                justifyContent: 'start',
                width: '100%',
              }}
            >
              <VailSelect
                control={form.control}
                label="Show Menu"
                name="settings.displayMenu"
                options={[
                  { label: 'Always', value: DisplayMenu.always },
                  { label: 'Multiple EGs', value: DisplayMenu.multipleEGs },
                  { label: 'Never', value: DisplayMenu.never },
                ]}
              />
              <VailSelect
                control={form.control}
                label="Display"
                name="settings.displayEGsBy"
                options={[
                  { label: 'Group', value: DisplayEGsBy.group },
                  { label: 'Individual', value: DisplayEGsBy.individual },
                ]}
              />
              <VailSelect
                control={form.control}
                label="Order"
                name="settings.orderBy"
                options={[
                  { label: 'Date', value: OrderBy.date },
                  { label: 'Specific', value: OrderBy.specific },
                ]}
              />
            </Stack>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="overline" width="100%">
                  Unstarted
                </Typography>
              </Grid>
              <Grid
                size={{
                  md: 4,
                  xs: 12,
                }}
              >
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={lang === orgLang}>
                      <VailTextField
                        control={form.control}
                        label="Pre Menu Text"
                        name={`settings.conversation.unstarted.preMenuText.${
                          orgLang as SupportedLocales
                        }.0`}
                        rules={{}}
                      />
                    </Case>
                  ))}
                </Switch>
              </Grid>
              <Grid
                size={{
                  md: 4,
                  xs: 12,
                }}
              >
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={lang === orgLang}>
                      <VailTextField
                        control={form.control}
                        label="Post Menu Text"
                        name={`settings.conversation.unstarted.postMenuText.${
                          orgLang as SupportedLocales
                        }.0`}
                        rules={{}}
                      />
                    </Case>
                  ))}
                </Switch>
              </Grid>
              <Grid
                size={{
                  md: 4,
                  xs: 12,
                }}
              >
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={lang === orgLang}>
                      <VailTextField
                        control={form.control}
                        label="Menu Button"
                        name={`settings.conversation.unstarted.button.${
                          orgLang as SupportedLocales
                        }`}
                      />
                    </Case>
                  ))}
                </Switch>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="overline" width="100%">
                  In progress
                </Typography>
              </Grid>
              <Grid
                size={{
                  md: 4,
                  xs: 12,
                }}
              >
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={lang === orgLang}>
                      <VailTextField
                        control={form.control}
                        label="Pre Menu Text"
                        name={`settings.conversation.inProgress.preMenuText.${
                          orgLang as SupportedLocales
                        }.0`}
                        rules={{}}
                      />
                    </Case>
                  ))}
                </Switch>
              </Grid>
              <Grid
                size={{
                  md: 4,
                  xs: 12,
                }}
              >
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={lang === orgLang}>
                      <VailTextField
                        control={form.control}
                        label="Post Menu Text"
                        name={`settings.conversation.inProgress.postMenuText.${
                          orgLang as SupportedLocales
                        }.0`}
                        rules={{}}
                      />
                    </Case>
                  ))}
                </Switch>
              </Grid>
              <Grid
                size={{
                  md: 4,
                  xs: 12,
                }}
              >
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={lang === orgLang}>
                      <VailTextField
                        control={form.control}
                        label="Menu button"
                        name={`settings.conversation.inProgress.button.${
                          orgLang as SupportedLocales
                        }`}
                      />
                    </Case>
                  ))}
                </Switch>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="overline" width="100%">
                  Complete
                </Typography>
              </Grid>
              <Grid
                size={{
                  md: 4,
                  xs: 12,
                }}
              >
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={lang === orgLang}>
                      <VailTextField
                        control={form.control}
                        label="Pre Menu Text"
                        name={`settings.conversation.complete.preMenuText.${
                          orgLang as SupportedLocales
                        }.0`}
                        rules={{}}
                      />
                    </Case>
                  ))}
                </Switch>
              </Grid>
              <Grid
                size={{
                  md: 4,
                  xs: 12,
                }}
              >
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={lang === orgLang}>
                      <VailTextField
                        control={form.control}
                        label="Post Menu Text"
                        name={`settings.conversation.complete.postMenuText.${
                          orgLang as SupportedLocales
                        }.0`}
                        rules={{}}
                      />
                    </Case>
                  ))}
                </Switch>
              </Grid>
              <Grid
                size={{
                  md: 4,
                  xs: 12,
                }}
              >
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={lang === orgLang}>
                      <VailTextField
                        control={form.control}
                        label="Menu button"
                        name={`settings.conversation.complete.button.${
                          orgLang as SupportedLocales
                        }`}
                      />
                    </Case>
                  ))}
                </Switch>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="overline" width="100%">
                  No Engagement Goals
                </Typography>
              </Grid>
              <Grid
                size={{
                  md: 4,
                  xs: 12,
                }}
              >
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={lang === orgLang}>
                      <VailTextField
                        control={form.control}
                        label="Text"
                        name={`settings.conversation.noEGs.text.${
                          orgLang as SupportedLocales
                        }.0`}
                        rules={{}}
                      />
                    </Case>
                  ))}
                </Switch>
              </Grid>

              <Grid
                size={{
                  md: 4,
                  xs: 12,
                }}
              >
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={lang === orgLang}>
                      <VailTextField
                        control={form.control}
                        label="Menu button"
                        name={`settings.conversation.noEGs.button.${
                          orgLang as SupportedLocales
                        }`}
                        rules={{}}
                      />
                    </Case>
                  ))}
                </Switch>
              </Grid>
              <Grid
                size={12}
                sx={{
                  display: 'flex',
                  flexDirection: ['column', null, 'row'],
                  gap: 2,
                }}
              >
                <VailSelect
                  control={form.control}
                  label="recipe"
                  name="settings.customRecipeOffRamp.recipeName"
                  options={recipesOptions}
                />
                <VailSelect
                  control={form.control}
                  label="recipe version"
                  name="settings.customRecipeOffRamp.version"
                  options={
                    historyItem.data?.map((history) => ({
                      label: history.name,
                      value: history.id,
                    })) ?? []
                  }
                />
                <Button
                  variant="outlined"
                  onClick={() =>
                    form.setValue('settings.customRecipeOffRamp', {
                      recipeName: '',
                      version: '',
                    })
                  }
                >
                  Clear Recipe
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </VailAccordion>
      </Stack>
    </FormProvider>
  )
}
